/**
 * SHX Reading text — accessible letter splitting + scroll-timeline slicing.
 *
 * The original sentence stays in the DOM (visually hidden) so screen readers
 * announce intact text; an aria-hidden split copy carries the animation. Each
 * letter gets its slice of the section's named view-timeline via an inline
 * `animation-range`, so during scroll ZERO JavaScript runs — the brightening
 * is a compositor-driven CSS animation (declared in the section's inline
 * style). Browsers without ViewTimeline (or reduced-motion users) simply show
 * the text at full opacity — no splitting happens at all.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ShxReadingText extends HTMLElement {
  connectedCallback() {
    // Entrance rise (any browser).
    if (REDUCED_MOTION.matches) {
      this.classList.add('is-entered');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.classList.add('is-entered');
            io.disconnect();
          }
        },
        { threshold: 0.35 }
      );
      io.observe(this.querySelector('.shx-rtext__frame') || this);
      this.enterObserver = io;
    }

    // Letter animation only where it can run as pure CSS.
    if (!('ViewTimeline' in window) || REDUCED_MOTION.matches) return;

    const source = this.querySelector('.shx-rtext__source');
    if (!source || this.querySelector('.shx-rtext__split')) return;

    const text = source.textContent.trim();
    if (!text) return;

    const split = document.createElement('span');
    split.className = 'shx-rtext__split';
    split.setAttribute('aria-hidden', 'true');

    const words = text.split(/\s+/);
    const totalChars = words.reduce((n, w) => n + w.length, 0);
    // Each letter brightens over its own window (the "Letter fade speed"
    // setting); starts are spread across the first 75% of the section's scroll
    // so the last letters finish early enough to be read before release.
    const windowSize = Math.max(4, Math.min(60, parseFloat(this.dataset.window) || 22));
    let index = 0;
    words.forEach((word, wi) => {
      const wordEl = document.createElement('span');
      wordEl.className = 'shx-rtext__word';
      for (const char of word) {
        const charEl = document.createElement('span');
        charEl.className = 'shx-rtext__char';
        charEl.textContent = char;
        const start = ((index / Math.max(1, totalChars)) * 75).toFixed(2);
        const end = Math.min(100, parseFloat(start) + windowSize).toFixed(2);
        charEl.style.animationRange = `contain ${start}% contain ${end}%`;
        wordEl.appendChild(charEl);
        index += 1;
      }
      split.appendChild(wordEl);
      if (wi < words.length - 1) split.appendChild(document.createTextNode(' '));
    });

    source.classList.add('visually-hidden');
    source.after(split);
    this.classList.add('is-split');
  }

  disconnectedCallback() {
    this.enterObserver?.disconnect();
  }
}

if (!customElements.get('shx-reading-text')) customElements.define('shx-reading-text', ShxReadingText);
